import React from 'react';
import PropTypes from 'prop-types';
import ArticleImage from '../../atoms/ArticleImage/ArticleImage';
import ArticleTitle from '../../atoms/ArticleTitle/ArticleTitle';
import ArticleInfo from '../../atoms/ArticleInfo/ArticleInfo';
import { useTheme } from '@mui/material/styles';
import { getOperationalText, operationalTypography } from '../../../design-system/tokens';

const SOCIAL_MEDIA_TYPES = ['YouTube', 'Twitter', 'Instagram', 'TikTok', 'Facebook', 'LinkedIn', 'Pinterest', 'Threads', 'Kwai'];

/**
 * ArticleHeader - Componente molecular que combina imagem, título e informações do artigo
 * 
 * Este componente segue o princípio do Atomic Design como uma "molécula".
 */
const ArticleHeader = ({
  article,
  variant = 'default',
  onTitleClick,
  className = '',
  ...props
}) => {
  const theme = useTheme();
  const { title, imageUrl, source, author, url } = article;
  const isDarkMode = theme.palette.mode === 'dark';
  const textTone = getOperationalText(isDarkMode);
  const cardType = operationalTypography.articleCard;
  const sourceTextColor = textTone.mutedStrong;
  const dateTextColor = textTone.date;

  // Fallback logic for site name and favicon (like ArticleCard.oldjs)
  const articleUrl = article.siteUrl || url;
  const siteName = article.siteName || article['site-name'] || article.site_name || source || (articleUrl ? (() => { try { return new URL(articleUrl).hostname.replace('www.', ''); } catch { return ''; } })() : '');
  // Para favicon: usa o favicon salvo (avatars TikTok/YouTube) ou a URL do site (deixa Favicon component decidir)
  const faviconSrc = article.favicon || article.faviicon || article.siteFavicon || article.site_favicon || articleUrl;

  // Fallback para data
  const date = article.date || article.publishedAt || article.createdAt;

  const styles = {
    header: {
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      ...(variant === 'list' && { marginBottom: '8px' }),
      ...(variant === 'minimal' && { alignItems: 'center', marginBottom: '8px' })
    },
    meta: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: variant === 'minimal' ? 'center' : 'flex-start'
    }
  };

  // Mapeamento de variantes do header para variantes dos componentes filhos
  const variantMappings = {
    default: { image: 'default', title: 'default', info: 'default' },
    minimal: { image: 'thumbnail', title: 'small', info: 'minimal' },
    detailed: { image: 'large', title: 'large', info: 'default' },
    list: { image: 'small', title: 'list', info: 'compact' }
  };

  const childVariants = variantMappings[variant] || variantMappings.default;

  return (
    <div
      className={`article-header article-header--${variant} ${className}`}
      style={styles.header}
      {...props}
    >
      {imageUrl && (
        <ArticleImage
          src={imageUrl}
          alt={title}
          variant={childVariants.image}
        />
      )}

      <div className="article-header__meta" style={{ ...styles.meta, minWidth: 0, overflow: 'hidden' }} translate="yes">
        <ArticleTitle
          variant={childVariants.title}
          onClick={onTitleClick ? (e) => {
            e.stopPropagation();
            onTitleClick();
          } : undefined}
        >
          {title}
        </ArticleTitle>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          minWidth: 0,
          color: textTone.muted,
          fontWeight: variant === 'detailed' ? cardType.source.fontWeight : 700,
          fontSize: variant === 'minimal' ? '0.7rem' : variant === 'detailed' ? cardType.source.fontSize : '0.82rem'
        }}>
          <div style={{ minWidth: 0, maxWidth: '100%' }}>
            <ArticleInfo
              source={siteName}
              author={author}
              favicon={faviconSrc}
              mediaType={article.mediaType}
              variant={childVariants.info}
              hideDate={true}
              showFavicon={false}
              faviconSize={variant === 'detailed' ? (SOCIAL_MEDIA_TYPES.includes(article.mediaType) ? 22 : 20) : 18}
              faviconGap={variant === 'detailed' ? (SOCIAL_MEDIA_TYPES.includes(article.mediaType) ? 8 : 7) : 7}
              sourceWeight={variant === 'detailed' ? cardType.source.fontWeight : 850}
              sourceColor={sourceTextColor}
            />
          </div>
          {date && (
            <div style={{
              fontSize: variant === 'minimal' ? '0.68rem' : variant === 'detailed' ? cardType.date.fontSize : '0.8rem',
              color: dateTextColor,
              fontWeight: variant === 'detailed' ? cardType.date.fontWeight : 650,
              marginTop: variant === 'detailed' ? cardType.date.marginTop : '3px',
              marginLeft: 0,
              opacity: variant === 'detailed' ? cardType.date.opacity : 0.92,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {(() => {
                try {
                  const dateObj = new Date(date);
                  return dateObj.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC'
                  }).replace('.', '.');
                } catch (error) {
                  return date;
                }
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ArticleHeader.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    source: PropTypes.string,
    author: PropTypes.string,
    publishedAt: PropTypes.string,
    url: PropTypes.string
  }).isRequired,
  variant: PropTypes.oneOf(['default', 'minimal', 'detailed', 'list']),
  onTitleClick: PropTypes.func,
  className: PropTypes.string
};

export default ArticleHeader;
